import React, { useEffect, useState } from "react";
import useAuthStore from "../../../../store/useAuthStore";
import { useNavigate } from "react-router-dom";
import getMonthsInRange from "../Month";
import { format } from "date-fns";
import useCompanyStore from "../../../../store/useCompanyStore";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Download, FileText, Table } from "lucide-react";

const safeString = (str) =>
  typeof str === "string" ? str.toLowerCase().trim() : "";

const ProfitAndLossStatement = ({ comparisonData }) => {
  const [accounts, setAccounts] = useState([]);
  const [incomeHeaders, setIncomeHeaders] = useState([]);
  const [expenseHeaders, setExpenseHeaders] = useState([]);
  const [incomeData, setIncomeData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [editing, setEditing] = useState(false);
  const [profitandLossSheet, setProfitandLossSheet] = useState(
    "Profit and Loss Report"
  );
  const [showExportOptions, setShowExportOptions] = useState(false);

  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { companyInfo } = useCompanyStore();

  const sumValues = (obj) =>
    obj ? Object.values(obj).reduce((sum, value) => sum + value, 0) : 0;

  // 1. Fetch accounts from chart of accounts (same pattern as ParentBS / ParentCF)
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await fetch("/api/getAllAccounts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch accounts");
        const data = await response.json();
        setAccounts(data.accounts || []);
      } catch (error) {
        console.error("Error fetching accounts:", error);
      }
    };
    fetchAccounts();
  }, [token]);

  // 2. Process comparison data using safeString matching (same as BS & CF)
  useEffect(() => {
    if (!accounts.length || !comparisonData.length) return;

    const incomeTypes = ["Revenue", "Sales", "Other Income"];
    const expenseTypes = [
      "Direct Expense",
      "Other Expense",
      "Operating Expenses",
      "Cost of Goods Sold (COGS)",
    ];

    // Build income headers from accounts
    const processedIncomeHeaders = accounts
      .filter((account) => incomeTypes.includes(account.type))
      .reduce((acc, curr) => {
        const existingHeader = acc.find((h) => h.name === curr.type);
        if (existingHeader) {
          existingHeader.subheader.push(curr);
        } else {
          acc.push({ name: curr.type, subheader: [curr] });
        }
        return acc;
      }, []);

    // Build expense headers from accounts
    const processedExpenseHeaders = accounts
      .filter((account) => expenseTypes.includes(account.type))
      .reduce((acc, curr) => {
        const existingHeader = acc.find((h) => h.name === curr.type);
        if (existingHeader) {
          existingHeader.subheader.push(curr);
        } else {
          acc.push({ name: curr.type, subheader: [curr] });
        }
        return acc;
      }, []);

    setIncomeHeaders(processedIncomeHeaders);
    setExpenseHeaders(processedExpenseHeaders);

    // For each period, for each account, find matching transactions using safeString
    const processedIncomeData = comparisonData.map((periodData) => {
      const transactionData = periodData.data || [];
      const grouped = {};

      processedIncomeHeaders.forEach((header) => {
        grouped[header.name] = {};
        header.subheader.forEach((account) => {
          const accountKey = safeString(`${account.code}-${account.name}`);
          const total = transactionData
            .filter(
              (item) =>
                item.type === "income" &&
                safeString(item.subheader) === accountKey
            )
            .reduce((sum, item) => sum + (item.amount || 0), 0);
          grouped[header.name][`${account.code}-${account.name}`] = total;
        });
      });

      return grouped;
    });

    const processedExpenseData = comparisonData.map((periodData) => {
      const transactionData = periodData.data || [];
      const grouped = {};

      processedExpenseHeaders.forEach((header) => {
        grouped[header.name] = {};
        header.subheader.forEach((account) => {
          const accountKey = safeString(`${account.code}-${account.name}`);
          const total = transactionData
            .filter(
              (item) =>
                item.type === "expense" &&
                safeString(item.subheader) === accountKey
            )
            .reduce((sum, item) => sum + (item.amount || 0), 0);
          grouped[header.name][`${account.code}-${account.name}`] = total;
        });
      });

      return grouped;
    });

    setIncomeData(processedIncomeData);
    setExpenseData(processedExpenseData);

    // Calculate profits
    const profitCalculations = comparisonData.map((_, periodIndex) => {
      const revenue = sumValues(
        processedIncomeData[periodIndex]?.["Revenue"] || {}
      );
      const sales = sumValues(
        processedIncomeData[periodIndex]?.["Sales"] || {}
      );
      const otherIncome = sumValues(
        processedIncomeData[periodIndex]?.["Other Income"] || {}
      );
      const directExpense = sumValues(
        processedExpenseData[periodIndex]?.["Direct Expense"] || {}
      );
      const cogs = sumValues(
        processedExpenseData[periodIndex]?.[
          "Cost of Goods Sold (COGS)"
        ] || {}
      );
      const operatingExpenses = sumValues(
        processedExpenseData[periodIndex]?.["Operating Expenses"] || {}
      );
      const otherExpense = sumValues(
        processedExpenseData[periodIndex]?.["Other Expense"] || {}
      );

      const grossProfit = revenue + sales - (directExpense + cogs);
      const netProfit =
        revenue +
        sales +
        otherIncome -
        (directExpense + cogs + operatingExpenses + otherExpense);

      return { grossProfit, netProfit };
    });

    setProfitData(profitCalculations);
  }, [comparisonData, accounts]);

  const handleSubheaderClick = (subheaderKey) => {
    const parts = subheaderKey.split("-");
    const code = parts[0];
    const name = parts.slice(1).join("-");

    const account = accounts.find(
      (acc) => `${acc.code}-${acc.name}` === subheaderKey
    );

    navigate("/TransactionRecord", {
      state: {
        accountName: name,
        accountType: account?.type || "",
        accountCode: code,
      },
    });
  };

  // Build subheader display key list from header (account objects -> "code-name" strings)
  const getSubheaderKeys = (header) =>
    header.subheader.map((acc) => `${acc.code}-${acc.name}`);

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;

    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(companyInfo?.name || "NO COMPANY NAME", pageWidth / 2, 25, {
      align: "center",
    });

    doc.setFontSize(14);
    doc.text(profitandLossSheet, pageWidth / 2, 35, { align: "center" });

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(
      `Generated on: ${format(new Date(), "MMMM dd, yyyy")}`,
      pageWidth / 2,
      42,
      { align: "center" }
    );

    const tableData = [];

    // Income sections
    tableData.push([
      {
        content: "Incomes",
        styles: { fontStyle: "bold", fillColor: [230, 240, 255] },
      },
      ...comparisonData.map(() => ""),
    ]);

    incomeHeaders.forEach((header) => {
      const subKeys = getSubheaderKeys(header);
      const filteredSubs = subKeys.filter((sub) =>
        comparisonData.some(
          (_, pi) =>
            incomeData[pi]?.[header.name]?.[sub] !== undefined &&
            incomeData[pi]?.[header.name]?.[sub] !== 0
        )
      );
      if (filteredSubs.length === 0) return;

      tableData.push([
        {
          content: header.name,
          styles: { fontStyle: "bold", textColor: [59, 130, 246] },
        },
        ...comparisonData.map(() => ""),
      ]);

      filteredSubs.forEach((sub) => {
        tableData.push([
          "  " + (sub.split("-").slice(1).join("-") || sub),
          ...comparisonData.map((_, pi) =>
            (incomeData[pi]?.[header.name]?.[sub] || 0).toFixed(2)
          ),
        ]);
      });

      tableData.push([
        { content: `Total ${header.name}`, styles: { fontStyle: "bold" } },
        ...comparisonData.map((_, pi) => ({
          content: sumValues(
            incomeData[pi]?.[header.name] || {}
          ).toFixed(2),
          styles: { fontStyle: "bold", textColor: [59, 130, 246] },
        })),
      ]);
    });

    // Expense sections
    tableData.push([
      {
        content: "Expenses",
        styles: { fontStyle: "bold", fillColor: [230, 240, 255] },
      },
      ...comparisonData.map(() => ""),
    ]);

    expenseHeaders.forEach((header) => {
      const subKeys = getSubheaderKeys(header);
      const filteredSubs = subKeys.filter((sub) =>
        comparisonData.some(
          (_, pi) =>
            expenseData[pi]?.[header.name]?.[sub] !== undefined &&
            expenseData[pi]?.[header.name]?.[sub] !== 0
        )
      );
      if (filteredSubs.length === 0) return;

      tableData.push([
        {
          content: header.name,
          styles: { fontStyle: "bold", textColor: [59, 130, 246] },
        },
        ...comparisonData.map(() => ""),
      ]);

      filteredSubs.forEach((sub) => {
        tableData.push([
          "  " + (sub.split("-").slice(1).join("-") || sub),
          ...comparisonData.map((_, pi) =>
            (expenseData[pi]?.[header.name]?.[sub] || 0).toFixed(2)
          ),
        ]);
      });

      tableData.push([
        { content: `Total ${header.name}`, styles: { fontStyle: "bold" } },
        ...comparisonData.map((_, pi) => ({
          content: sumValues(
            expenseData[pi]?.[header.name] || {}
          ).toFixed(2),
          styles: { fontStyle: "bold", textColor: [59, 130, 246] },
        })),
      ]);
    });

    // Profit rows
    tableData.push([
      {
        content: "Gross Profit",
        styles: { fontStyle: "bold", fillColor: [230, 240, 255] },
      },
      ...profitData.map((p) => ({
        content: p.grossProfit.toFixed(2),
        styles: {
          fontStyle: "bold",
          textColor: p.grossProfit >= 0 ? [34, 197, 94] : [239, 68, 68],
        },
      })),
    ]);
    tableData.push([
      {
        content: "Net Profit",
        styles: { fontStyle: "bold", fillColor: [240, 253, 244] },
      },
      ...profitData.map((p) => ({
        content: p.netProfit.toFixed(2),
        styles: {
          fontStyle: "bold",
          textColor: p.netProfit >= 0 ? [34, 197, 94] : [239, 68, 68],
        },
      })),
    ]);

    doc.autoTable({
      head: [
        [
          "Account",
          ...comparisonData.map((period) =>
            getMonthsInRange(period.period)
          ),
        ],
      ],
      body: tableData,
      startY: 50,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { halign: "left" },
        ...Object.fromEntries(
          Array.from({ length: comparisonData.length }, (_, i) => [
            i + 1,
            { halign: "right" },
          ])
        ),
      },
      didDrawPage: function (data) {
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${pageNumber}`,
          pageWidth - margin,
          doc.internal.pageSize.height - 10,
          { align: "right" }
        );
      },
    });

    doc.save(
      `${profitandLossSheet}_${format(new Date(), "yyyy-MM-dd")}.pdf`
    );
    setShowExportOptions(false);
  };

  // Export to CSV
  const exportToCSV = () => {
    let csvContent = "";
    csvContent += `${companyInfo?.name || "NO COMPANY NAME"}\n`;
    csvContent += `${profitandLossSheet}\n`;
    csvContent += `Generated on: ${format(new Date(), "MMMM dd, yyyy")}\n\n`;

    const headers = [
      "Account",
      ...comparisonData.map((period) => getMonthsInRange(period.period)),
    ];
    csvContent += headers.join(",") + "\n";

    csvContent += "INCOME" + ",".repeat(comparisonData.length) + "\n";
    incomeHeaders.forEach((header) => {
      const subKeys = getSubheaderKeys(header);
      const filteredSubs = subKeys.filter((sub) =>
        comparisonData.some(
          (_, pi) =>
            incomeData[pi]?.[header.name]?.[sub] !== undefined &&
            incomeData[pi]?.[header.name]?.[sub] !== 0
        )
      );
      if (filteredSubs.length === 0) return;

      csvContent += header.name + ",".repeat(comparisonData.length) + "\n";
      filteredSubs.forEach((sub) => {
        csvContent +=
          [
            sub.split("-").slice(1).join("-") || sub,
            ...comparisonData.map((_, pi) =>
              (incomeData[pi]?.[header.name]?.[sub] || 0).toFixed(2)
            ),
          ].join(",") + "\n";
      });
      csvContent +=
        [
          `Total ${header.name}`,
          ...comparisonData.map((_, pi) =>
            sumValues(incomeData[pi]?.[header.name] || {}).toFixed(2)
          ),
        ].join(",") + "\n";
    });

    csvContent +=
      "\nEXPENSES" + ",".repeat(comparisonData.length) + "\n";
    expenseHeaders.forEach((header) => {
      const subKeys = getSubheaderKeys(header);
      const filteredSubs = subKeys.filter((sub) =>
        comparisonData.some(
          (_, pi) =>
            expenseData[pi]?.[header.name]?.[sub] !== undefined &&
            expenseData[pi]?.[header.name]?.[sub] !== 0
        )
      );
      if (filteredSubs.length === 0) return;

      csvContent += header.name + ",".repeat(comparisonData.length) + "\n";
      filteredSubs.forEach((sub) => {
        csvContent +=
          [
            sub.split("-").slice(1).join("-") || sub,
            ...comparisonData.map((_, pi) =>
              (expenseData[pi]?.[header.name]?.[sub] || 0).toFixed(2)
            ),
          ].join(",") + "\n";
      });
      csvContent +=
        [
          `Total ${header.name}`,
          ...comparisonData.map((_, pi) =>
            sumValues(expenseData[pi]?.[header.name] || {}).toFixed(2)
          ),
        ].join(",") + "\n";
    });

    csvContent += "\n";
    csvContent +=
      [
        "Gross Profit",
        ...profitData.map((p) => p.grossProfit.toFixed(2)),
      ].join(",") + "\n";
    csvContent +=
      ["Net Profit", ...profitData.map((p) => p.netProfit.toFixed(2))].join(
        ","
      ) + "\n";

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute(
      "download",
      `${profitandLossSheet}_${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  const renderTableRows = (headers, data, type) =>
    headers.map((header, headerIndex) => {
      const subKeys = getSubheaderKeys(header);

      const filteredSubheaders = subKeys.filter((sub) =>
        comparisonData.some(
          (_, periodIndex) =>
            data[periodIndex]?.[header.name]?.[sub] !== undefined &&
            data[periodIndex]?.[header.name]?.[sub] !== 0
        )
      );

      const rows = filteredSubheaders.map((sub, subIndex) => (
        <tr
          key={`sub-${header.name}-${subIndex}`}
          className="hover:bg-blue-50"
        >
          <td
            className="pl-3 text-gray-700 hover:text-blue-500 hover:underline cursor-pointer p-1.5"
            onClick={() => handleSubheaderClick(sub)}
          >
            {sub.split("-").slice(1).join("-") || sub}
          </td>
          {comparisonData.map((_, periodIndex) => (
            <td
              key={`sub-${header.name}-${subIndex}-${periodIndex}`}
              className="text-center"
            >
              {(data[periodIndex]?.[header.name]?.[sub] || 0).toFixed(2)}
            </td>
          ))}
        </tr>
      ));

      if (rows.length === 0) return null;

      return (
        <React.Fragment key={`header-${headerIndex}`}>
          <tr key={`header-row-${headerIndex}`}>
            <td
              colSpan={comparisonData.length + 1}
              className="text-blue-500 text-lg mb-4"
            >
              {header.name}
            </td>
          </tr>
          {rows}
          <tr
            className="font-bold text-gray-600 border-t border-dashed mb-4 pl-3"
            key={`total-row-${headerIndex}`}
          >
            <td className="pb-4 pl-3">Total {header.name}</td>
            {comparisonData.map((_, periodIndex) => (
              <td
                key={`total-${headerIndex}-${periodIndex}`}
                className="text-blue-500 text-center pb-2 mb-4"
              >
                {sumValues(
                  data[periodIndex]?.[header.name] || {}
                ).toFixed(2)}
              </td>
            ))}
          </tr>
        </React.Fragment>
      );
    });

  return (
    <div className="bg-white p-2">
      <div className="text-center mb-3 p-2">
        <h1 className="text-xl font-semibold">
          {companyInfo ? companyInfo.name : "NO COMPANY NAME"}
        </h1>
        <div className="mt-1 flex justify-center">
          <h1
            className="text-center font-bold text-gray-700 cursor-pointer"
            onClick={() => setEditing(true)}
          >
            {editing ? (
              <input
                type="text"
                value={profitandLossSheet}
                onChange={(e) => setProfitandLossSheet(e.target.value)}
                onBlur={() => setEditing(false)}
                autoFocus
                className="border-b-2 border-gray-300 rounded-none w-full"
              />
            ) : (
              profitandLossSheet
            )}
          </h1>
        </div>
        <h6 className="text-gray-600 mt-2 font-medium">
          {format(new Date(), "MMMM dd, yyyy")}
        </h6>

        {/* Export Button */}
        <div className="relative mt-4 flex justify-center">
          <button
            onClick={() => setShowExportOptions(!showExportOptions)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 font-medium"
          >
            <Download size={18} />
            Export Report
          </button>

          {showExportOptions && (
            <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[180px]">
              <button
                onClick={exportToPDF}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <FileText size={16} className="text-red-500" />
                <span>Export as PDF</span>
              </button>
              <button
                onClick={exportToCSV}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <Table size={16} className="text-green-500" />
                <span>Export as CSV</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 border rounded bg-white ">
        <table className="table-auto w-full ">
          <thead className="border-b border-gary-200">
            <tr>
              <th className="text-left">Account</th>
              {comparisonData.map((period, index) => (
                <th key={index} className="text-center">
                  {getMonthsInRange(period.period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderTableRows(incomeHeaders, incomeData, "income")}
            {renderTableRows(expenseHeaders, expenseData, "expense")}
            <tr className="font-bold  border-t pt-4">
              <td>Gross Profit</td>
              {profitData.map((profit, index) => (
                <td
                  key={index}
                  className={`text-center ${
                    profit.grossProfit >= 0
                      ? "text-green-700"
                      : "text-red-700"
                  }`}
                >
                  {profit.grossProfit.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="font-bold">
              <td>Net Profit</td>
              {profitData.map((profit, index) => (
                <td
                  key={index}
                  className={`text-center pt-2 ${
                    profit.netProfit >= 0
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                >
                  {profit.netProfit.toFixed(2)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Click outside to close export options */}
      {showExportOptions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowExportOptions(false)}
        />
      )}
    </div>
  );
};

export default ProfitAndLossStatement;
